import React, {
 useContext, useEffect, useState
} from 'react';
import {
 assistantInstanceService, opcodeAssistantService
} from 'shared/api';
import {
 useParams
} from 'react-router-dom';
import {
 Button
} from 'shared/components/ui/button';
import {
 Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from 'shared/components/ui/dialog';
import {
 ScrollArea
} from 'shared/components/ui/scroll-area';
import AssistantForm from './AssistantForm';
import {
 Context
} from 'shared/context/GlobalState';
import {
 Assistant, makeEmptyAssistant
} from '../types/builderAssistant.types';

interface ManageAssistantDialogProps {
  show: boolean;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
  selectedCard: Assistant;
  setAssistantInstances: React.Dispatch<React.SetStateAction<Assistant[]>>;
}

const ManageAssistantDialog = ({
 show, setShow, selectedCard, setAssistantInstances,
}: ManageAssistantDialogProps) => {
  const [formData, setFormData] = useState<Partial<Assistant>>(() =>
      ({
 ...makeEmptyAssistant('', ''),
...selectedCard
})
  );
  const [btnLoading, setBtnLoading] = useState(false);
  const {
 addNewNotifcation
} = useContext(Context);
  const {
 oragID, subOragID
} = useParams<{ oragID: string; subOragID: string }>();

  useEffect(() => {
    if (show) {
      setFormData({
 ...makeEmptyAssistant(oragID || '', subOragID || ''),
...selectedCard
});
    }
  }, [show, selectedCard, oragID, subOragID]);

  const handleUpdateAssistant = async () => {
    if (!formData.title) {
      return addNewNotifcation('Assistant Name is required', 'warning');
    }
    if (!formData.default_opcode || (formData.opcodes?.length ?? 0) <= 0) {
      return addNewNotifcation('Please pick opcodes and a default', 'warning');
    }

    setBtnLoading(true);
    try {
      await assistantInstanceService.updateAssistantInstance({
        id: selectedCard.assistant_id,
        organizationId: oragID,
        subOrganizationId: subOragID,
        title: formData.title,
        description: formData.description,
      } as any);

      // Full-doc update; Server strips audit fields before Joi validates.
      // `assistant_id` is the already-known slug — used as the path param via
      // `assistantId`. Body round-trips the rich shape.
      const payload = {
        ...formData,
        org_id: oragID,
        sub_org_id: subOragID,
        assistantId: selectedCard.assistant_id,
      };
      const res = await opcodeAssistantService.updateOpcodeAssistant(payload as any);
      const updated = (res as any)?.data || payload;

      setAssistantInstances(prev =>
        prev.map(item => (item.assistant_id === selectedCard.assistant_id ? {
 ...(updated as Assistant)
} : item))
      );
      addNewNotifcation('Assistant updated successfully', 'success');
      setShow(false);
    } catch (err: any) {
      console.error('update error', err);
      const msg = err?.response?.data || err?.message || 'Something went wrong';
      addNewNotifcation(typeof msg === 'string' ? msg : 'Something went wrong', 'danger');
    } finally {
      setBtnLoading(false);
    }
  };

  return (
      <Dialog open={show} onOpenChange={setShow}>
          <DialogContent className="w-full max-w-3xl bg-white rounded-lg shadow-lg">
              <DialogHeader>
                  <DialogTitle className="text-xl font-semibold">Update assistant</DialogTitle>
                  <DialogDescription className="text-sm text-muted-foreground">
                      Edit any section — all fields round-trip as the full rich shape.
                  </DialogDescription>
              </DialogHeader>

              <ScrollArea className="max-h-[500px] px-1 py-2">
                  <AssistantForm form={formData} setForm={setFormData} />
              </ScrollArea>

              <DialogFooter className="flex justify-end space-x-3 mt-4">
                  <Button variant="outline" onClick={() => setShow(false)}>Cancel</Button>
                  <Button disabled={btnLoading} onClick={handleUpdateAssistant}>
                      {btnLoading ? 'Updating…' : 'Update assistant'}
                  </Button>
              </DialogFooter>
          </DialogContent>
      </Dialog>
  );
};

export default ManageAssistantDialog;
